import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Input, InputNumber, Row, Select, Space, Spin, Statistic,
  Table, Tag, Typography, notification,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import OrganizationAutocomplete from '../../features/orgs/components/OrganizationAutocomplete';
import { canViewCareProgramUtilization } from './careProgramPermissions';

const { Text, Title } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'not_done', label: 'Not done' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_COLORS = {
  not_done: 'default',
  in_progress: 'processing',
  completed: 'success',
};

/**
 * Admin program utilization table.
 * @param {object} props
 * @param {string} props.programCode — required when fixedProgram
 * @param {boolean} [props.fixedProgram] — hide program picker (program detail / org row)
 * @param {number|string} [props.orgId] — lock to one org (org detail); hides org picker & org column
 * @param {string} [props.orgName]
 * @param {boolean} [props.embedded]
 * @param {string} [props.title]
 */
const CareProgramUtilizationPanel = ({
  programCode: programCodeProp,
  fixedProgram = false,
  orgId = null,
  orgName = '',
  embedded = false,
  title,
}) => {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();

  const canAccess = useMemo(
    () => canViewCareProgramUtilization(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const [programCode, setProgramCode] = useState(programCodeProp ?? null);
  const [orgFilter, setOrgFilter] = useState(orgId ? { org_id: orgId, org_name: orgName } : null);
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, period: null });

  useEffect(() => {
    if (programCodeProp) setProgramCode(programCodeProp);
  }, [programCodeProp]);

  useEffect(() => {
    if (orgId) setOrgFilter({ org_id: orgId, org_name: orgName });
  }, [orgId, orgName]);

  const fetchUtilization = useCallback(async (page = 1, limit = 20) => {
    if (!programCode) return;
    setLoading(true);
    try {
      const params = { page, limit };
      const scopedOrgId = orgId ?? orgFilter?.org_id;
      if (scopedOrgId) params.org_id = scopedOrgId;
      if (cycleYear) params.cycle_year = cycleYear;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;

      const parsed = lib.parseApiEnvelope(
        await lib.getAdminCareProgramUtilization(programCode, params),
      );
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to load utilization' });
        setSummary(null);
        setRows([]);
        return;
      }

      const data = parsed.data ?? {};
      setSummary(data.summary ?? null);
      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list);
      const m = parsed.meta ?? data.meta ?? {};
      setMeta({
        page: Number(m.page ?? page),
        limit: Number(m.limit ?? limit),
        total: Number(m.total ?? list.length),
        period: m.period ?? null,
      });
    } catch (e) {
      notification.error({ message: e?.message || 'Network error' });
      setSummary(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [programCode, orgId, orgFilter, cycleYear, statusFilter, search]);

  useEffect(() => {
    if (programCode && canAccess) {
      fetchUtilization(1, meta.limit);
    }
  }, [programCode, orgId, orgFilter, cycleYear, statusFilter, search, canAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const showOrgColumn = !orgId;

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Client',
        key: 'client',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Text strong>{row.client_name ?? '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{row.insurance_no ?? '—'}</Text>
          </Space>
        ),
      },
    ];
    if (showOrgColumn) {
      cols.push({
        title: 'Organization',
        key: 'org',
        render: (_, row) => row.org_name ?? row.organization_name ?? (row.org_id ? `#${row.org_id}` : '—'),
      });
    }
    cols.push(
      {
        title: 'Status',
        dataIndex: 'status',
        width: 120,
        render: (s, row) => (
          <Tag color={STATUS_COLORS[s] || 'default'}>
            {row.status_label ?? s ?? '—'}
          </Tag>
        ),
      },
      {
        title: 'Completed',
        dataIndex: 'date_completed',
        width: 140,
        render: (v) => v || '—',
      },
      {
        title: 'Facility',
        dataIndex: 'facility_name',
        render: (v, row) => v ?? row.facility ?? '—',
      },
    );
    return cols;
  }, [showOrgColumn]);

  if (!canAccess) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You need care_programs.read_all_utilization (or admin.*) to view program utilization."
      />
    );
  }

  if (!programCode) {
    return <Alert type="info" message="Program code is required to load utilization." />;
  }

  const heading = title ?? (orgId
    ? `Utilization — ${orgName || `org #${orgId}`}`
    : 'Cross-organization performance');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!embedded && (
        <div>
          <Title level={4} style={{ margin: 0 }}>{heading}</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {orgId
              ? 'Roster and completion for this organization only (program must be enabled on the org).'
              : 'Ops view across all organizations. Filter by org to drill down.'}
            {meta.period?.label ? ` Period: ${meta.period.label}.` : ''}
          </Text>
        </div>
      )}

      <Card bordered={false} size="small">
        <Row gutter={[16, 16]} align="middle">
          {!fixedProgram && (
            <Col xs={24} md={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>Program</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="Select program"
                value={programCode}
                onChange={setProgramCode}
                disabled
              />
            </Col>
          )}
          {!orgId && (
            <Col xs={24} md={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>Organization (optional)</Text>
              <div style={{ marginTop: 4 }}>
                <OrganizationAutocomplete
                  placeholder="All organizations"
                  onSelect={(org) => setOrgFilter(org)}
                />
                {orgFilter?.org_name && (
                  <Button type="link" size="small" onClick={() => setOrgFilter(null)} style={{ padding: 0, marginTop: 4 }}>
                    Clear filter ({orgFilter.org_name})
                  </Button>
                )}
              </div>
            </Col>
          )}
          <Col xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>Cycle year</Text>
            <InputNumber
              style={{ width: '100%', marginTop: 4 }}
              min={2000}
              max={2100}
              value={cycleYear}
              onChange={(v) => setCycleYear(v ?? new Date().getFullYear())}
            />
          </Col>
          <Col xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={setStatusFilter}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input.Search
              placeholder="Search name or insurance number"
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => setSearch(v.trim())}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchUtilization(meta.page, meta.limit)}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {summary && (
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title="Roster" value={summary.roster_count ?? 0} /></Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title="Completed" value={summary.completed_count ?? 0} valueStyle={{ color: '#3f8600' }} /></Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title="Not done" value={summary.not_done_count ?? 0} /></Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title="In progress" value={summary.in_progress_count ?? 0} /></Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic title="Completion rate" value={summary.completion_rate ?? 0} suffix="%" precision={1} />
            </Card>
          </Col>
        </Row>
      )}

      <Spin spinning={loading}>
        <Table
          size="small"
          rowKey={(r) => r.insurance_no ?? r.checkin_id ?? `${r.client_name}-${r.org_id}`}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: meta.page,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => fetchUtilization(page, pageSize),
          }}
          locale={{ emptyText: 'No utilization rows for this filter' }}
        />
      </Spin>
    </Space>
  );
};

export default CareProgramUtilizationPanel;
